import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    ItemComponentTypeBreadcrumbs,
    ItemComponentTypeCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as ItemComponentTypeActions from "../../../actions/itemComponentType";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { LookupsLayout } from "../../index";

import "./ItemComponentTypePage.scss";

interface ItemComponentTypePageProps {
    form: Dtos.ItemComponentType & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    itemComponentType: Dtos.ItemComponentType;
    lookups: Dtos.Lookup[];
    loadingItemComponentType: boolean;
    loadItemComponentTypeFailure: boolean;
    loadItemComponentTypeErrorCode: string;
    savingItemComponentType: boolean;
    saveItemComponentTypeSuccess: boolean;
    saveItemComponentTypeFailure: boolean;
    creatingItemComponentType: boolean;
    createItemComponentTypeSuccess: boolean;
    validatingItemComponentType: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearItemComponentType: ItemComponentTypeActions.IItemComponentTypeClearActionCreator;
    loadItemComponentType: ItemComponentTypeActions.IItemComponentTypeLoadByIdActionCreator;
    saveItemComponentType: ItemComponentTypeActions.IItemComponentTypeSaveActionCreator;
    createItemComponentType: ItemComponentTypeActions.IItemComponentTypeCreateActionCreator;
    validateItemComponentType: ItemComponentTypeActions.IItemComponentTypeValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.itemComponentType";

const ItemComponentTypePage: FC<ItemComponentTypePageProps> = ({
    form,
    formState,
    formProperties,
    itemComponentType,
    lookups,
    loadingItemComponentType,
    loadItemComponentTypeFailure,
    loadItemComponentTypeErrorCode,
    savingItemComponentType,
    saveItemComponentTypeSuccess,
    saveItemComponentTypeFailure,
    creatingItemComponentType,
    createItemComponentTypeSuccess,
    validatingItemComponentType,
    validationFailures,
    permissions,
    clearItemComponentType,
    loadItemComponentType,
    saveItemComponentType,
    createItemComponentType,
    validateItemComponentType,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { itemcomponenttypesId } = useParams<{
        itemcomponenttypesId: string;
    }>();
    const itemComponentTypeIdNumber = parseInt(itemcomponenttypesId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.ItemComponentType = {} as Dtos.ItemComponentType;

        loadForm(
            reduxFormName,
            Object.assign({ ...itemComponentType }, extraData)
        );
    };


    // Load item component type
    useEffect(() => {
        if (
            itemComponentTypeIdNumber &&
            !itemComponentType &&
            !loadingItemComponentType
        ) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(
                ItemComponentTypeBreadcrumbs(itemComponentTypeIdNumber)
            );
            loadItemComponentType(itemComponentTypeIdNumber);
        } else if (
            !itemComponentTypeIdNumber &&
            !creatingItemComponentType &&
            !createItemComponentTypeSuccess
        ) {
            createItemComponentType();
            setBreadcrumbs(ItemComponentTypeCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (itemComponentType && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        itemComponentTypeIdNumber,
        itemComponentType,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        ItemComponentTypeBreadcrumbs,
        loadItemComponentType,
        createItemComponentType,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingItemComponentType,
        creatingItemComponentType,
        createItemComponentType,
        ItemComponentTypeCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadItemComponentTypeFailure) {
            navigate(`/error/${loadItemComponentTypeErrorCode}`);
        }
    }, [
        loadItemComponentTypeFailure,
        loadItemComponentTypeErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (itemComponentTypeIdNumber && itemComponentType?.value) {
            setBreadcrumbs(
                ItemComponentTypeBreadcrumbs(
                    itemComponentTypeIdNumber,
                    itemComponentType?.value
                )
            );
        }
    }, [
        itemComponentTypeIdNumber,
        itemComponentType,
        setBreadcrumbs,
        ItemComponentTypeBreadcrumbs,
    ]);

    // Reload item component type after saving
    useEffect(() => {
        if (!savingItemComponentType && saveItemComponentTypeSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Item component type saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/general/itemcomponenttypes";
                navigate(url);
            } else if (!itemComponentTypeIdNumber) {
                let url: string = `/settings/general/itemcomponenttypes/${itemComponentType?.id}`;
                navigate(url);
                clearItemComponentType();
            }
        }
    }, [
        savingItemComponentType,
        saveItemComponentTypeSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingItemComponentType && saveItemComponentTypeFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingItemComponentType, saveItemComponentTypeFailure]);

    // Cleanup effect to clear the item component type on component unmount
    useEffect(() => {
        return () => {
            clearItemComponentType();
            resetForm(reduxFormName);
        };
    }, [clearItemComponentType, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/general/itemcomponenttypes";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.ItemComponentType) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.ItemComponentType = {} as Dtos.ItemComponentType;

            await saveItemComponentType(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.ItemComponentType = {} as Dtos.ItemComponentType;

            if (form && hasBeenSubmitted && !validatingItemComponentType) {
                await validateItemComponentType(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.ItemComponentTypesUpdate,
                Dtos.Permission.ItemComponentTypesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingItemComponentType ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "itemComponentType",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "itemComponentType",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "itemComponentType",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "itemComponentType",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingItemComponentType,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <LookupsLayout
            loading={loadingItemComponentType}
            permission={Dtos.Permission.ItemComponentTypesView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<ItemComponentTypePageProps> => {
    let itemComponentType: Dtos.ItemComponentType | undefined = !(
        state.itemComponentTypes.formData instanceof Array
    )
        ? state.itemComponentTypes.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];
    return {
        itemComponentType: itemComponentType!,
        loadingItemComponentType:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Pending,
        loadItemComponentTypeFailure:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Failure,
        loadItemComponentTypeErrorCode:
            state.itemComponentTypes.loadState.errorCode,
        savingItemComponentType:
            state.itemComponentTypes.saveState &&
            state.itemComponentTypes.saveState.status == RequestState.Pending,
        saveItemComponentTypeSuccess:
            state.itemComponentTypes.saveState &&
            state.itemComponentTypes.saveState.status == RequestState.Success,
        saveItemComponentTypeFailure:
            state.itemComponentTypes.saveState &&
            state.itemComponentTypes.saveState.status == RequestState.Failure,
        creatingItemComponentType:
            state.itemComponentTypes.createState &&
            state.itemComponentTypes.createState.status == RequestState.Pending,
        createItemComponentTypeSuccess:
            state.itemComponentTypes.createState &&
            state.itemComponentTypes.createState.status == RequestState.Success,
        validatingItemComponentType:
            state.itemComponentTypes.validateState &&
            state.itemComponentTypes.validateState.status ==
                RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.itemComponentType,
        formState: state.itemComponentTypes.formState,
        formProperties: state.itemComponentTypes.formProperties,
        validationFailures: itemComponentType
            ? state.itemComponentTypes.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<ItemComponentTypePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadItemComponentType: bindActionCreators(
            ItemComponentTypeActions.LoadItemComponentTypeById,
            dispatch
        ),
        clearItemComponentType: bindActionCreators(
            ItemComponentTypeActions.Clear,
            dispatch
        ),
        saveItemComponentType: bindActionCreators(
            ItemComponentTypeActions.SaveItemComponentType,
            dispatch
        ),
        createItemComponentType: bindActionCreators(
            ItemComponentTypeActions.CreateItemComponentType,
            dispatch
        ),
        validateItemComponentType: bindActionCreators(
            ItemComponentTypeActions.ValidateItemComponentType,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ItemComponentTypePage);
